import type {
  BehaviorFunnelMetric,
  BehaviorMetric,
  BehaviorOrderChannel,
} from '../../../gateways/breadstack/customer/behaviorOverviewApis'
import { formatPct } from './behaviorMetrics'

// Pure display helpers for the Behavior › Overview sub-tab, fed by the
// `behavior-overview` endpoint. Free of React so edge cases are unit-tested.

export type MetricDeltaDir = 'up' | 'down' | 'invUp' | 'invDown' | 'flat'

export interface MetricDelta {
  text: string
  dir: MetricDeltaDir
}

// KPI footer badge from the backend's relative % change. `undefined` when the
// change is null (previous window was 0) — the card hides the badge rather
// than showing ∞ / a misleading 0%. `inverted` flips the colour sense for
// "lower is better" metrics (Cart Abandonment): a fall reads green.
export const metricDelta = (
  metric: BehaviorMetric | null | undefined,
  { inverted = false }: { inverted?: boolean } = {},
): MetricDelta | undefined => {
  const change = metric?.percentageIncrease
  if (typeof change !== 'number' || !Number.isFinite(change)) return undefined
  if (change === 0) return { text: '0%', dir: 'flat' }
  const text = formatPct(Math.abs(change)) ?? '0%'
  if (change > 0) return { text, dir: inverted ? 'invUp' : 'up' }
  return { text, dir: inverted ? 'invDown' : 'down' }
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

// `2026-09-08` → `Sep 8`. Parsed as a plain local calendar date — never via
// `new Date(str)`, which treats `YYYY-MM-DD` as UTC midnight and shifts the day
// back for viewers west of UTC. Returns the input unchanged if malformed.
export const formatTrendDate = (date: string | null | undefined): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date ?? '')
  if (!m) return date ?? ''
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  if (Number.isNaN(d.getTime())) return date ?? ''
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`
}

export type ChannelLabel = '' | 'Online' | 'In-store' | 'Online + In-store'

// Trend tooltip channel row: which channels the day's orders came through.
export const channelLabel = (
  channels: BehaviorOrderChannel[] | null | undefined,
): ChannelLabel => {
  const online = channels?.includes('online') ?? false
  const pos = channels?.includes('pos') ?? false
  if (online && pos) return 'Online + In-store'
  if (online) return 'Online'
  if (pos) return 'In-store'
  return ''
}

// Per-series chart ceiling: 20% headroom over the max, never below `floor` so
// an all-zero / tiny series still draws as a flat line at the bottom.
export const seriesMax = (values: number[], floor: number): number => {
  let max = 0
  for (const v of values) if (Number.isFinite(v) && v > max) max = v
  return Math.max(max * 1.2, floor)
}

const FUNNEL_LABELS: Partial<Record<BehaviorFunnelMetric, string>> = {
  'Viewed Product': 'Viewed Product Detail',
}

// Mockup copy for funnel steps; everything else passes through verbatim.
export const funnelLabel = (metric: BehaviorFunnelMetric): string =>
  FUNNEL_LABELS[metric] ?? metric

const intFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
})

// `1,234`. Rounds; non-finite → `0`.
export const formatInt = (value: number | null | undefined): string =>
  intFormatter.format(
    typeof value === 'number' && Number.isFinite(value) ? value : 0,
  )
